/**
 * matching.js — Matching fuzzy des modeles d'imprimantes
 *
 * Audit terrain : Wissam saisit "HP M 402" ou "Brother HL-L3270".
 * Cette lib resout vers les references du catalogue compat-laser.
 *
 * Stratégie matching (par modele compatible) :
 *   1. Normalisation : lowercase, sans accents, ponctuation -> espaces
 *   2. Substring exact apres normalisation -> score 1.0 (confiance max)
 *   3. Tokenisation : split sur espaces/tirets/slashes, drop "series" et bruit
 *   4. Score par token : exact > prefixe/inclusion > Levenshtein normalise
 *   5. Score modele = moyenne des meilleurs scores par token requete
 *   6. Score ref = max des scores de ses modeles compatibles
 *
 * Alerte hard : si la requete contient Xerox / Ricoh / OKI,
 * retour { matches: [], uncoveredBrand: 'xerox' } (parc non adressable).
 *
 * Zero dependance externe. Levenshtein implemente inline.
 */

// ============================================================
// Constants
// ============================================================

// Marques laser explicitement NON couvertes par le catalogue
// (88 refs en couvrent 7 : HP, Lexmark, Brother, Epson, Canon, Kyocera, Samsung)
const UNCOVERED_BRANDS = ['xerox', 'ricoh', 'oki'];

// Tokens supprimes au matching (bruit redondant dans la base)
const NOISE_TOKENS = new Set(['series', 'serie', 'imprimante', '']);

// Score minimum pour considerer un modele comme une vraie correspondance
// Tune empirique : 0.5 evite les faux positifs sur queries vagues comme "HP".
const SCORE_THRESHOLD = 0.5;

// Nombre max de matches retournes (top-N, tri decroissant par score)
const MAX_MATCHES = 20;

// ============================================================
// Normalisation
// ============================================================

/**
 * Normalise une chaine pour le matching :
 *   - lowercase
 *   - decompose et retire les diacritiques (e -> e, c -> c)
 *   - remplace la ponctuation par des espaces, sauf - et /
 *   - compacte les espaces multiples
 *   - trim
 *
 * Exemple : "HP LaserJet Pro M 402 Series" -> "hp laserjet pro m 402 series"
 *           "Canon i-SENSYS LBP-630"        -> "canon i-sensys lbp-630"
 */
export function normalizeModel(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .normalize('NFD')
    // Classe de caracteres combining marks U+0300 -> U+036F (diacritiques)
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\w\s\-/]/g, ' ')
    .replace(/\s+/g, ' ')
    // Colle lettre+chiffre separes par espace : "m 402" -> "m402", "hl l 3270" -> "hl l3270"
    // Permet de matcher "HP M402" tape par Wissam avec "HP M 402 Series" du catalogue.
    // Appliquer 2 fois pour gerer chaines type "m 4 02".
    .replace(/([a-z])\s+(\d)/g, '$1$2')
    .replace(/([a-z])\s+(\d)/g, '$1$2')
    .trim();
}

/**
 * Decompose une chaine normalisee en tokens significatifs.
 * Split sur espaces, tirets, slashes ET frontiere lettre<->chiffre.
 * Drop le bruit (NOISE_TOKENS).
 *
 * Le split lettre<->chiffre permet de matcher les saisies terrain
 * sans espace ("M402", "HL-L3270") avec les references officielles
 * qui ont des espaces ("HP M 402", "HL-L 3270").
 *
 * Exemples :
 *   "hp laserjet pro m 402 series" -> ['hp', 'laserjet', 'pro', 'm', '402']
 *   "hl-l3270"                     -> ['hl', 'l', '3270']
 *   "ecosys m2540"                 -> ['ecosys', 'm', '2540']
 */
export function tokenize(str) {
  return normalizeModel(str)
    .split(/[\s\-/]+/)
    .flatMap((t) => t.split(/(?<=\D)(?=\d)|(?<=\d)(?=\D)/))
    .filter((t) => t.length > 0 && !NOISE_TOKENS.has(t));
}

// ============================================================
// Levenshtein (distance d'edition, implementation single-row)
// ============================================================

/**
 * Distance de Levenshtein entre deux chaines (operations insertion/suppression/substitution).
 * Implementation O(m*n) en temps, O(min(m,n)) en memoire.
 * Symetrique : lev(a,b) = lev(b,a).
 */
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  // Toujours iterer sur la plus courte en colonne (optimisation memoire)
  if (a.length > b.length) [a, b] = [b, a];

  const m = a.length;
  const n = b.length;
  let prev = new Array(m + 1);
  let curr = new Array(m + 1);

  for (let i = 0; i <= m; i++) prev[i] = i;

  for (let j = 1; j <= n; j++) {
    curr[0] = j;
    for (let i = 1; i <= m; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[i] = Math.min(
        prev[i] + 1,           // suppression
        curr[i - 1] + 1,       // insertion
        prev[i - 1] + cost,    // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[m];
}

// ============================================================
// Scoring
// ============================================================

/**
 * Score de similarite entre deux tokens (0 a 1).
 *   1.0  : identiques
 *   0.85 : inclusion (preferons un cote contient l'autre)
 *   sim  : Levenshtein normalise (1 - dist/maxLen), seuil 0.7 sinon 0
 */
function tokenScore(qTok, tTok) {
  if (qTok === tTok) return 1.0;

  // Tokens courts (1-2 chars) : on exige l'egalite, sinon trop bruite
  if (qTok.length < 3 || tTok.length < 3) {
    return qTok === tTok ? 1.0 : 0;
  }

  // Inclusion (ex: "lbp" inclus dans "lbp-630" apres tokenize)
  if (tTok.includes(qTok) || qTok.includes(tTok)) return 0.85;

  // Fuzzy via Levenshtein normalise
  const dist = levenshtein(qTok, tTok);
  const maxLen = Math.max(qTok.length, tTok.length);
  const sim = 1 - dist / maxLen;
  return sim >= 0.7 ? sim * 0.8 : 0; // scale a 0.8 max pour ne pas concurrencer inclusion
}

/**
 * Score d'un modele cible vs une requete utilisateur (0 a 1).
 *
 * Court-circuit haute confiance : si la query normalisee est substring du modele
 * normalise (ex: "m 402" dans "hp laserjet pro m 402 series") -> 1.0.
 *
 * Sinon : tokenisation + best-match par token requete, moyenne ponderee.
 *
 * Exporte pour debug / tests.
 */
export function scoreMatch(query, target) {
  const qNorm = normalizeModel(query);
  const tNorm = normalizeModel(target);

  if (!qNorm) return 0;
  if (tNorm.includes(qNorm)) return 1.0;

  const qTokens = tokenize(query);
  const tTokens = tokenize(target);

  if (qTokens.length === 0 || tTokens.length === 0) return 0;

  // Pour chaque token requete, on cherche le meilleur score dans les tokens cibles
  let sum = 0;
  let matched = 0;
  for (const qt of qTokens) {
    let best = 0;
    for (const tt of tTokens) {
      const s = tokenScore(qt, tt);
      if (s > best) best = s;
    }
    sum += best;
    if (best > 0) matched++;
  }

  // Penalite si peu de tokens matches (eviter qu'un seul token couvre tout)
  const coverage = matched / qTokens.length;
  const avg = sum / qTokens.length;

  return avg * coverage;
}

// ============================================================
// API publique
// ============================================================

/**
 * Accepte soit l'objet wrapper { tarif_date, total_laser, compatibilites: [...] }
 * (forme actuelle de fg_compatibilite_laser.json), soit directement un Array.
 * Normalise en Array pour le reste de la lib.
 *
 * @returns {Array} liste des refs compatibilite
 */
function normalizeCompatData(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.compatibilites)) return data.compatibilites;
  return [];
}

/**
 * Detecte une marque non couverte (Xerox / Ricoh / OKI) dans une requete.
 *
 * Pour 'oki' : on exige un match au niveau token (apres tokenize) pour eviter
 * les faux positifs (mais "okidata" doit aussi matcher, donc startsWith ok).
 *
 * @returns {'xerox' | 'ricoh' | 'oki' | null}
 */
export function detectUncoveredBrand(query) {
  const norm = normalizeModel(query);
  if (!norm) return null;

  const tokens = tokenize(query);

  for (const brand of UNCOVERED_BRANDS) {
    if (brand === 'oki') {
      // Token-level pour eviter false positives genre "okidata" est OK ici,
      // c'est bien la marque Oki Data. Mais on bloque substring "okay", "tokio"...
      if (tokens.some((t) => t === 'oki' || t.startsWith('oki'))) {
        return brand;
      }
    } else {
      // xerox, ricoh : substring sur la chaine normalisee suffit (assez specifiques)
      if (norm.includes(brand)) return brand;
    }
  }
  return null;
}

/**
 * Extrait la liste dedoublonnee des modeles d'imprimantes connus dans le catalogue.
 * Utilisee pour l'autocomplete du champ saisie audit.
 *
 * Chaque entree du JSON peut etre un compound separateur " / " (ex:
 * "Brother DCP-L 5500/6600 Series / HL-L 5000/... Series / MFC-L 5700/... Series").
 * On split sur " / " pour produire des suggestions plus courtes et lisibles.
 * Le "/" interne (5500/6600) est conserve, c'est la notation officielle.
 *
 * Tri alphabetique pour facilite de scan.
 *
 * @param {Array} compatLaserData - donnees fg_compatibilite_laser.json
 * @returns {string[]} liste triee
 */
export function extractKnownModels(compatLaserData) {
  const refs = normalizeCompatData(compatLaserData);
  const set = new Set();
  for (const ref of refs) {
    const models = ref.modeles_compatibles || [];
    for (const model of models) {
      if (typeof model !== 'string') continue;
      const chunks = model.split(/\s+\/\s+/);
      for (const chunk of chunks) {
        const trimmed = chunk.trim();
        if (trimmed) set.add(trimmed);
      }
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
}

/**
 * Suggere des modeles d'autocomplete pour une saisie en cours.
 * Filtre simple par substring normalise sur la liste des modeles connus.
 * Limite a `limit` resultats pour ne pas saturer le dropdown terrain.
 *
 * @param {string} query - saisie utilisateur (peut etre partielle, "hp m4")
 * @param {string[]} knownModels - sortie de extractKnownModels()
 * @param {number} [limit=10]
 * @returns {string[]}
 */
export function suggestModels(query, knownModels, limit = 10) {
  const qNorm = normalizeModel(query);
  if (!qNorm || qNorm.length < 2) return [];

  // Decompose en tokens, chaque token doit etre present (substring) dans la cible.
  // Permet "HP M402" -> "HP LaserJet Pro M402 Series" sans exiger l'ordre strict.
  const qTokens = qNorm.split(/[\s\-/]+/).filter((t) => t.length >= 2);

  const suggestions = [];
  for (const m of knownModels) {
    const mNorm = normalizeModel(m);
    // Fast path : substring direct (rare maintenant avec normalisation m402)
    if (mNorm.includes(qNorm)) {
      suggestions.push(m);
    } else if (qTokens.length > 1 && qTokens.every((t) => mNorm.includes(t))) {
      suggestions.push(m);
    }
    if (suggestions.length >= limit) break;
  }
  return suggestions;
}

/**
 * Match principale : retourne les refs du catalogue compat-laser
 * compatibles avec le modele d'imprimante saisi.
 *
 * @param {string} query - modele d'imprimante saisi (ex: "HP M 402", "Brother HL-L3270")
 * @param {Array} compatLaserData - donnees fg_compatibilite_laser.json
 * @returns {{
 *   matches: Array<{ ref: object, matchedModel: string, score: number }>,
 *   uncoveredBrand: 'xerox' | 'ricoh' | 'oki' | null,
 *   queryNormalized: string
 * }}
 *
 * Si uncoveredBrand est non-null : alerte hard cote UI, ne PAS promettre
 * la livraison de consommables pour ce parc.
 */
export function matchPrinterModel(query, compatLaserData) {
  const queryNormalized = normalizeModel(query);

  if (!queryNormalized) {
    return { matches: [], uncoveredBrand: null, queryNormalized: '' };
  }

  const uncoveredBrand = detectUncoveredBrand(query);
  if (uncoveredBrand) {
    return { matches: [], uncoveredBrand, queryNormalized };
  }

  const refs = normalizeCompatData(compatLaserData);
  if (refs.length === 0) {
    console.warn('[matching] matchPrinterModel: aucune donnee compat (data vide ou mal forme)');
    return { matches: [], uncoveredBrand: null, queryNormalized };
  }

  const candidates = [];
  for (const ref of refs) {
    const models = ref.modeles_compatibles || [];
    const brand = ref.marque_imprimante || '';
    let bestScore = 0;
    let bestModel = null;
    for (const model of models) {
      if (typeof model !== 'string') continue;
      const chunks = model.split(/\s+\/\s+/);
      for (const chunk of chunks) {
        // On prefixe la marque imprimante dans la cible : l'utilisateur tape
        // souvent "Kyocera FS-1320" mais le modele en base est juste "FS-1320/1370"
        // (marque stockee separement dans ref.marque_imprimante).
        const target = brand ? `${brand} ${chunk}` : chunk;
        const s = scoreMatch(query, target);
        if (s > bestScore) {
          bestScore = s;
          bestModel = chunk; // on garde le chunk seul pour l'affichage UI
        }
      }
    }
    if (bestScore >= SCORE_THRESHOLD) {
      candidates.push({ ref, matchedModel: bestModel, score: bestScore });
    }
  }

  candidates.sort((a, b) => b.score - a.score);
  return {
    matches: candidates.slice(0, MAX_MATCHES),
    uncoveredBrand: null,
    queryNormalized,
  };
}

/**
 * Regroupe une liste de matches par marque imprimante (pour affichage UI).
 *
 * @returns {{ [brand: string]: Array<match> }}
 */
export function groupMatchesByBrand(matches) {
  const groups = {};
  for (const m of matches) {
    const brand = m.ref.marque_imprimante || 'Inconnue';
    if (!groups[brand]) groups[brand] = [];
    groups[brand].push(m);
  }
  return groups;
}

/**
 * Helper synthese : extrait des matches une vue resumee par type de consommable.
 * Utile pour le module Audit (afficher "X toners, Y tambours dispos pour ce modele").
 *
 * @returns {{ Toner: number, Tambour: number, Collecteur: number, total: number }}
 */
export function summarizeMatchesByType(matches) {
  const summary = { Toner: 0, Tambour: 0, Collecteur: 0, total: matches.length };
  for (const m of matches) {
    const type = m.ref.type;
    if (type === 'Toner' || type === 'Tambour' || type === 'Collecteur') {
      summary[type]++;
    }
  }
  return summary;
}
